import * as T from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';
import {attachPileSurface,samplePilePoint} from './pile-surface.js';
import {PileCamera} from './pile-camera.js';
import {PileSoftBody} from './pile-soft-body.js';
import {AuroraSurface} from './aurora-field.js';
import {makeToyBox} from './toy-box.js';
import {updateSurfaceBounds} from './surface-deformation.js';
import {finishToyMaterial} from './toy-materials.js';
import {CollectionPile,directionSupport} from './collection-pile.js';
import {shellPieceIndex} from './crackle-shell.js';
import {modelHref,catalogItem} from './catalog.js';
import {disposeModel} from './model-cache.js';

const BOX_FADE_START=3.2,BOX_FADE_END=4.4;

// Share authored materials and merge meshes, then deform each toy with its own cage.
export function makePileTemplate(source,{waxDamage=[],waxBroken=false,kind,variant}={}){
  source.updateMatrixWorld(true);
  const box=new T.Box3().setFromObject(source),size=box.getSize(new T.Vector3()),center=box.getCenter(new T.Vector3());
  const scale=1/Math.max(size.x,size.y,size.z);
  const transform=new T.Matrix4().makeScale(scale,scale,scale).multiply(new T.Matrix4().makeTranslation(-center.x,-center.y,-center.z));
  const groups=new Map();
  source.traverse(node=>{
    if(!node.isMesh)return;
    const shell=shellPieceIndex(node.name);if(shell>=0&&(waxBroken||waxDamage.includes(shell)))return;
    const original=node.material;
    if(Array.isArray(original))return;
    let group=groups.get(original.uuid);
    if(!group){
      const material=finishToyMaterial(original.clone(),null,null,{kind,mobile:true,variant});
      group={geometries:[],material};groups.set(original.uuid,group);
    }
    let geometry=node.geometry.clone();geometry.applyMatrix4(transform.clone().multiply(node.matrixWorld));
    // Retain shared vertices; expanding every triangle multiplies phone memory.
    if(!geometry.index)geometry.setIndex(Array.from({length:geometry.attributes.position.count},(_,i)=>i));
    for(const name of Object.keys(geometry.attributes))if(!['position','normal','color','uv'].includes(name))geometry.deleteAttribute(name);
    if(!geometry.attributes.normal)geometry.computeVertexNormals();
    group.geometries.push(geometry);
  });
  const result=new T.Group();
  for(const {geometries,material} of groups.values()){
    const geometry=mergeGeometries(geometries);for(const item of geometries)item.dispose();
    if(!geometry){material.dispose();continue;}
    result.add(new T.Mesh(geometry,material));
  }
  return result;
}

export class PileView{
  constructor(environment){
    this.scene=new T.Scene();this.scene.background=new T.Color('#f7f4ef');this.scene.environment=environment;this.scene.environmentIntensity=.7;
    this.scene.add(new T.HemisphereLight('#fff8ef','#b5a4c0',1.6));
    const light=new T.DirectionalLight('#fff9ee',2.3);light.position.set(-3,6,5);this.scene.add(light);
    this.controls=new PileCamera();this.camera=this.controls.camera;this.pointers=new Map();this.mode='touch';this.box=makeToyBox();this.scene.add(this.box.group);this.loader=new GLTFLoader();this.sequence=0;this.models=[];this.templates=[];this.sources=[];this.dirty=true;
    this.ray=new T.Raycaster();this.pointer=new T.Vector2();this.plane=new T.Plane();this.hit=new T.Vector3();
    this.shadowGeometry=new T.CircleGeometry(1,24);this.shadowMaterial=new T.ShaderMaterial({transparent:true,depthWrite:false,uniforms:{boxIndex:{value:0},boxOpacity:{value:1}},vertexShader:'uniform int boxIndex; uniform float boxOpacity; varying vec2 vUv; varying float vOpacity; void main(){vUv=uv;vOpacity=gl_InstanceID==boxIndex?boxOpacity:1.;gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(position,1.);}',fragmentShader:'varying vec2 vUv; varying float vOpacity; void main(){float r=length(vUv-.5)*2.;gl_FragColor=vec4(.32,.28,.31,.15*vOpacity*(1.-smoothstep(.1,1.,r)));}'});
  }
  resize(viewport){this.viewport=viewport;this.controls.resize(viewport.width,viewport.height);this.dirty=true;}
  setMode(mode){if(this.mode!==mode){this.release();this.mode=mode;}}
  zoom(direction){this.controls.magnify(direction==='in'?1.18:1/1.18);this.dirty=true;}
  resetCamera(){this.controls.reset();this.dirty=true;}
  wheel(event){event.preventDefault();this.controls.magnify(Math.exp(-event.deltaY*.001));this.dirty=true;}
  clear(){
    this.release();this.sequence++;
    for(const item of this.models){this.scene.remove(item.group);for(const mesh of item.group.children){mesh.geometry.dispose();mesh.material.dispose();}}
    for(const template of this.templates)template.traverse(node=>{node.geometry?.dispose();node.material?.dispose();});
    for(const source of this.sources)disposeModel(source);this.sources=[];
    if(this.shadows){this.scene.remove(this.shadows);this.shadows.dispose();this.shadows=null;}
    this.models=[];this.templates=[];this.physics=null;this.pourTime=0;this.box.setPourProgress(0);this.box.setOpacity(1);this.shadowMaterial.uniforms.boxOpacity.value=1;this.dirty=true;
  }
  async load(toys,reducedMotion){
    this.clear();const sequence=this.sequence;
    const templates=new Map();
    // Load sequentially to bound decode/compilation spikes and cancellation work.
    for(const kind of new Set(toys.map(toy=>toy.kind))){
      const gltf=await this.loader.loadAsync(modelHref(kind));
      if(sequence!==this.sequence){disposeModel(gltf);return false;}
      this.sources.push(gltf);
      for(const toy of toys.filter(toy=>toy.kind===kind)){
        const key=JSON.stringify([kind,toy.waxBroken,toy.waxDamage,toy.variant]);
        if(templates.has(key))continue;
        const template=makePileTemplate(gltf.scene,toy);templates.set(key,template);this.templates.push(template);
      }
    }
    this.pourTime=reducedMotion?2.2:0;this.box.setPourProgress(reducedMotion?1:0);
    const shapes=Object.fromEntries(toys.map(toy=>{
      const template=templates.get(JSON.stringify([toy.kind,toy.waxBroken,toy.waxDamage,toy.variant]));
      return [toy.id,new T.Box3().setFromObject(template).getSize(new T.Vector3()).toArray()];
    }));
    this.physics=new CollectionPile(toys,{reducedMotion,shapes,halfWidth:1.7,halfDepth:1.2,radiusScale:1.4,pourOrigin:()=>this.box.getMouth()});
    for(const body of this.physics.bodies){
      const toy=toys.find(toy=>toy.id===body.id);
      const group=templates.get(JSON.stringify([body.kind,toy.waxBroken,toy.waxDamage,toy.variant])).clone(true);
      for(const mesh of group.children){mesh.geometry=mesh.geometry.clone();mesh.userData.rest=mesh.geometry.attributes.position.array.slice();}
      const bounds=new T.Box3().setFromObject(group),soft=new PileSoftBody(bounds.min.toArray(),bounds.max.toArray(),catalogItem(body.kind)?.physics??catalogItem(body.kind)?.material);
      for(const mesh of group.children){
        // Material.clone does not copy shader callbacks. Reinstall each toy's
        // finish with its own volume uniforms before adding cage deformation.
        mesh.material=finishToyMaterial(mesh.material.clone(),this.scene.environment,bounds,{kind:body.kind,mobile:true,variant:toy.variant});
        mesh.userData.surface=attachPileSurface(mesh.material,soft.body);mesh.frustumCulled=false;
      }
      group.userData.id=body.id;group.scale.setScalar(body.radius*2);this.scene.add(group);
      const aurora=body.kind==='auroragel'?new AuroraSurface(bounds.min.toArray(),bounds.max.toArray(),group.children,{size:10}):null;
      this.models.push({body,group,soft,bounds,aurora,contact:new T.Vector3(0,0,.4)});
    }
    this.shadowMaterial.uniforms.boxIndex.value=this.models.length;
    this.shadows=new T.InstancedMesh(this.shadowGeometry,this.shadowMaterial,this.models.length+1);this.shadows.frustumCulled=false;this.scene.add(this.shadows);
    this.update(0);this.dirty=true;return true;
  }
  rayFor(event){const v=this.viewport;this.pointer.set((event.clientX-v.left)/v.width*2-1,-(event.clientY-v.top)/v.height*2+1);this.ray.setFromCamera(this.pointer,this.camera);}
  releaseHeld(){this.physics?.release();this.held?.item.soft.release();this.autoSqueeze?.item.soft.release();this.held=null;this.autoSqueeze=null;this.dirty=true;}
  down(event,canvas){
    if(![0,2].includes(event.button)||!this.models.length)return;
    event.preventDefault();canvas.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});
    if(this.pointers.size>1){this.releaseHeld();this.orbitPointer=null;return;}
    this.rayFor(event);
    // Accurate picking uses the deformed surface only on pointer-down; frame
    // rendering leaves the rest buffers on the GPU and updates cage uniforms.
    const out=[0,0,0],restPoint=[0,0,0];
    for(const item of this.models)for(const mesh of item.group.children){
      const p=mesh.geometry.attributes.position;
      for(let i=0;i<p.count;i++){restPoint[0]=p.getX(i);restPoint[1]=p.getY(i);restPoint[2]=p.getZ(i);samplePilePoint(item.soft.body,restPoint,out);p.setXYZ(i,...out);}updateSurfaceBounds(mesh.geometry);
    }
    const hits=this.ray.intersectObjects([...this.models.filter(item=>item.body.visible!==false).map(item=>item.group),...(this.box.group.visible?[this.box.group]:[])],true);
    for(const item of this.models)for(const mesh of item.group.children){mesh.geometry.attributes.position.array.set(mesh.userData.rest);updateSurfaceBounds(mesh.geometry);}

    if(this.mode==='orbit'||event.button===2||!hits.length||hits[0].object.parent===this.box.group){this.orbitPointer=event.pointerId;canvas.style.cursor='grabbing';return;}
    const item=this.models.find(item=>item.group===hits[0].object.parent);if(!item)return;
    canvas.dataset.pileGrabs=String(Number(canvas.dataset.pileGrabs??0)+1);
    const world=hits[0].point.clone(),local=item.group.worldToLocal(world.clone());
    const normal=hits[0].face.normal.clone().transformDirection(hits[0].object.matrixWorld).transformDirection(item.group.matrixWorld.clone().invert());
    this.held={item,pointerId:event.pointerId,world,normal,offset:new T.Vector3(item.body.x,item.body.y,item.body.z).sub(world)};
    item.contact.copy(local);item.soft.grab(local.toArray());
    this.plane.setFromNormalAndCoplanarPoint(this.camera.getWorldDirection(new T.Vector3()),world);
    this.physics.drag(item.body.id,item.body);this.dirty=true;canvas.style.cursor='grabbing';
  }
  move(event,canvas){
    const previous=this.pointers.get(event.pointerId);
    if(!previous){canvas.style.cursor=this.mode==='orbit'?'grab':'default';return;}
    if(this.pointers.size>1){
      const other=[...this.pointers.entries()].find(([id])=>id!==event.pointerId)[1];
      const oldDistance=Math.hypot(previous.x-other.x,previous.y-other.y),newDistance=Math.hypot(event.clientX-other.x,event.clientY-other.y);
      this.controls.orbit((event.clientX-previous.x)*.5,(event.clientY-previous.y)*.5);
      if(oldDistance>10&&newDistance>10)this.controls.magnify(newDistance/oldDistance);
    }else if(this.orbitPointer===event.pointerId){this.controls.orbit(event.clientX-previous.x,event.clientY-previous.y);}
    else if(this.held?.pointerId===event.pointerId){
      this.rayFor(event);
      if(this.ray.ray.intersectPlane(this.plane,this.hit)){
        this.held.world.copy(this.hit);this.hit.add(this.held.offset);this.hit.y=Math.min(2.8,this.hit.y);
        this.physics.drag(this.held.item.body.id,this.hit);
      }
    }
    this.pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});this.dirty=true;
    canvas.dataset.pileCamera=JSON.stringify([this.controls.yaw,this.controls.pitch,this.controls.zoom].map(n=>+n.toFixed(3)));
  }
  up(event){
    this.pointers.delete(event.pointerId);if(this.held?.pointerId===event.pointerId)this.releaseHeld();
    this.orbitPointer=this.pointers.size===1?[...this.pointers.keys()][0]:null;this.dirty=true;
  }
  release(){this.releaseHeld();this.pointers?.clear();this.orbitPointer=null;}
  squeeze(id){
    const item=this.models.find(item=>item.body.id===id);if(!item||item.body.visible===false)return;
    this.releaseHeld();const point=new T.Vector3(0,item.bounds.max.y*.45,item.bounds.max.z);
    item.soft.grab(point.toArray());this.autoSqueeze={item,point,age:0};this.dirty=true;
  }
  update(dt){
    if(!this.dirty&&!this.held&&!this.autoSqueeze&&this.physics?.sleeping&&this.pourTime>=BOX_FADE_END&&this.models.every(item=>item.soft.sleeping&&!item.aurora?.active))return false;
    const pouring=this.physics&&this.pourTime<BOX_FADE_END;
    if(pouring){
      this.pourTime=Math.min(BOX_FADE_END,this.pourTime+dt);
      if(this.pourTime-dt<1.65)this.box.setPourProgress(Math.min(1,this.pourTime/1.65));
      const fade=T.MathUtils.clamp((this.pourTime-BOX_FADE_START)/(BOX_FADE_END-BOX_FADE_START),0,1);
      this.box.setOpacity(1-fade*fade*(3-2*fade));this.shadowMaterial.uniforms.boxOpacity.value=this.box.opacity;
    }
    const wasActive=this.physics?.active,active=this.physics?.step(dt);let deforming=false;
    const matrix=new T.Matrix4(),rotation=new T.Quaternion().setFromEuler(new T.Euler(-Math.PI/2,0,0));
    const point=new T.Vector3(),inverse=new T.Matrix4();
    for(let index=0;index<this.models.length;index++){
      const item=this.models[index],{body,group,soft}=item;
      group.visible=true;
      if(body.visible===false){
        point.set((index%2-.5)*.72,Math.floor(index/4)*.40-.08,(Math.floor(index/2)%2-.5)*.48);
        group.position.copy(this.box.group.localToWorld(point));group.quaternion.copy(this.box.group.quaternion).multiply(new T.Quaternion().setFromEuler(new T.Euler(body.rotation.x,body.rotation.y,body.rotation.z)));
        group.updateMatrixWorld(true);for(const mesh of group.children)mesh.material.userData.updateVolume?.(group.matrixWorld);
        this.shadows?.setMatrixAt(index,matrix.makeScale(0,0,0));continue;
      }
      group.rotation.set(body.rotation.x,body.rotation.y,body.rotation.z);
      group.position.set(body.x,body.y,body.z);group.updateMatrixWorld(true);inverse.copy(group.matrixWorld).invert();
      for(const mesh of group.children)mesh.material.userData.updateVolume?.(group.matrixWorld);
      if(this.held?.item===item){
        point.copy(this.held.world).applyMatrix4(inverse).addScaledVector(this.held.normal,-.16);
        soft.move(point.toArray());
      }
      if(this.autoSqueeze?.item===item){
        this.autoSqueeze.age+=dt;const amount=Math.sin(Math.min(1,this.autoSqueeze.age/.8)*Math.PI)*.22;
        soft.move(this.autoSqueeze.point.clone().add(new T.Vector3(0,0,-amount)).toArray());
        if(this.autoSqueeze.age>=.8){soft.release();this.autoSqueeze=null;}
      }
      const contacts=(body.contacts??[]).map(contact=>({
        point:point.fromArray(contact.restPoint??contact.point).applyMatrix4(inverse).toArray(),normal:new T.Vector3(...contact.normal).transformDirection(inverse).toArray(),depth:contact.depth/(body.radius*2),load:contact.load,
      }));
      const wasSoft=soft.active,changed=soft.step(dt,contacts);
      const colorChanged=item.aurora?.step(dt,soft.body.hold);
      if(colorChanged)item.aurora.update();
      deforming=deforming||colorChanged||item.aurora?.active;
      this.physics.setShape(body.id,soft.shape,soft.center);
      if(wasSoft||changed||this.dirty){
        deforming=deforming||wasSoft||changed;
        for(const mesh of group.children)mesh.userData.surface.update();
      }
      const spread=1+Math.min(3,body.y)*.12;
      point.fromArray(soft.center).applyMatrix4(group.matrixWorld);point.y=.005;
      const width=(directionSupport(body,[1,0,0])+directionSupport(body,[-1,0,0]))*.5;
      const depth=(directionSupport(body,[0,0,1])+directionSupport(body,[0,0,-1]))*.5;
      matrix.compose(point,rotation,new T.Vector3(width*spread,depth*spread,1));this.shadows?.setMatrixAt(index,matrix);
    }
    if(this.shadows){
      matrix.compose(new T.Vector3(-1.65,.004,-.9),rotation,new T.Vector3(1.3,.9,1));this.shadows.setMatrixAt(this.models.length,matrix);this.shadows.instanceMatrix.needsUpdate=true;
    }
    const render=!!(this.dirty||pouring||wasActive||active||this.physics?.active||deforming);this.dirty=false;return render;
  }
}
